import { ScheduleRemark, ScheduleRemarkType } from "../services/apiService";
import { WeekInfo } from "./weekUtils";

export const SCHEDULE_REMARK_WEEKDAYS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

export type ScheduleRemarkWeekday = typeof SCHEDULE_REMARK_WEEKDAYS[number];

export interface ScheduleRemarkChipDisplay {
  icon: string;
  variant: ScheduleRemarkType;
  chipText: string;
  tooltip: string;
}

export function formatScheduleRemarkWeek(weekId: number): string {
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;
  return `W${week} ${year}`;
}

export function formatScheduleRemarkRange(
  startWeek: number,
  endWeek?: number | null
): string {
  const start = formatScheduleRemarkWeek(startWeek);
  if (endWeek == null) {
    return `${start} – ongoing`;
  }
  return `${start} – ${formatScheduleRemarkWeek(endWeek)}`;
}

export function formatScheduleRemarkDays(days?: string | null): string {
  if (!days?.trim()) {
    return "";
  }
  return days
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean)
    .join(", ");
}

export function parseScheduleRemarkDays(days?: string | null): ScheduleRemarkWeekday[] {
  if (!days?.trim()) {
    return [];
  }

  const selected = new Set(
    days.split(",").map((day) => day.trim()).filter(Boolean)
  );

  return SCHEDULE_REMARK_WEEKDAYS.filter((day) => selected.has(day));
}

export function joinScheduleRemarkDays(
  days: ScheduleRemarkWeekday[]
): string {
  return SCHEDULE_REMARK_WEEKDAYS.filter((day) => days.includes(day)).join(",");
}

export function getRemarkShortText(text: string, maxLength = 20): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}…`;
}

export function getRemarkTooltip(remark: ScheduleRemark): string {
  return `${remark.text.trim()}\n${formatScheduleRemarkRange(
    remark.start_week,
    remark.end_week
  )}`;
}

export function getRemarkChipDisplay(
  remark: ScheduleRemark
): ScheduleRemarkChipDisplay {
  const remarkType = remark.remark_type || "note";
  const formattedDays = formatScheduleRemarkDays(remark.days);
  const tooltip = getRemarkTooltip(remark);

  if (remarkType === "off" && formattedDays) {
    return {
      icon: "pi pi-calendar-minus",
      variant: "off",
      chipText: formattedDays,
      tooltip,
    };
  }

  if (remarkType === "constraint" && formattedDays) {
    return {
      icon: "pi pi-exclamation-triangle",
      variant: "constraint",
      chipText: formattedDays,
      tooltip,
    };
  }

  return {
    icon: "pi pi-info-circle",
    variant: "note",
    chipText: formattedDays || getRemarkShortText(remark.text),
    tooltip,
  };
}

export function formatScheduleRemarkType(type: ScheduleRemarkType): string {
  switch (type) {
    case "off":
      return "Off";
    case "constraint":
      return "Constraint";
    case "note":
      return "Note";
    default:
      return type;
  }
}

export function getRemarksForView(
  remarks: ScheduleRemark[],
  weeks: WeekInfo[]
): ScheduleRemark[] {
  if (weeks.length === 0 || remarks.length === 0) {
    return [];
  }

  const viewStart = Number(weeks[0].weekKey);
  const viewEnd = Number(weeks[weeks.length - 1].weekKey);

  return remarks.filter(
    (remark) =>
      remark.start_week <= viewEnd &&
      (remark.end_week == null || remark.end_week >= viewStart)
  );
}

export function weekIdFromParts(year: number, week: number): number {
  return parseInt(`${year}${week.toString().padStart(2, "0")}`, 10);
}

export function weekPartsFromId(weekId: number): { year: number; week: number } {
  return {
    year: Math.floor(weekId / 100),
    week: weekId % 100,
  };
}
